import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Film } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import useSEO from '../hooks/useSEO';
import { MaskedLines, Kicker, Reveal } from '../components/Reveal';
import { PROJECT_CATEGORIES, projectUrl } from '../data/projects';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND_COLORS = { media: '#14DA64', souvenir: '#0CBEDD' };

function ProjectCard({ p, catLabels, lang, viewLabel }) {
  return (
    <Link
      to={projectUrl(p.slug, lang)}
      data-testid={`project-card-${p.slug}`}
      data-cursor={viewLabel}
      className="group block"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-zinc-950">
        {p.coverThumb || p.cover ? (
          <img
            src={p.coverThumb || p.cover}
            alt={p.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-zinc-800">
            <Film className="h-10 w-10" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <span className="absolute right-4 top-4 flex h-10 w-10 translate-y-2 items-center justify-center rounded-full bg-white text-black opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
          <ArrowUpRight className="h-4 w-4" />
        </span>
        {p.videos?.length > 0 && (
          <span className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-black/70 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white backdrop-blur-sm">
            <Film className="h-3 w-3" /> {catLabels.video}
          </span>
        )}
      </div>
      <div className="mt-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: BRAND_COLORS[p.brand] || '#FFCE00' }}>
            {p.categories.map((c) => catLabels[c]).filter(Boolean).join(' · ')}
          </p>
          <h3 className="mt-2 font-heading text-xl font-extrabold uppercase tracking-tight text-white transition-colors duration-300 sm:text-2xl">
            {p.name}
          </h3>
          {p.tagline && <p className="mt-1.5 text-sm text-zinc-400">{p.tagline[lang]}</p>}
        </div>
        {p.year && <span className="shrink-0 font-mono text-[11px] text-zinc-600">{p.year}</span>}
      </div>
      <span
        className="mt-4 block h-px w-full origin-left scale-x-0 transition-transform duration-700 ease-out group-hover:scale-x-100"
        style={{ backgroundColor: BRAND_COLORS[p.brand] || '#FFCE00' }}
      />
    </Link>
  );
}

export default function PortfolioPage() {
  const { lang, t } = useSite();
  useSEO(t.meta.portfolio, 'portfolio', lang);
  const [cat, setCat] = useState('all');
  const [uploaded, setUploaded] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/projects`)
      .then((r) => (r.ok ? r.json() : { projects: [] }))
      .then((d) => {
        if (!alive) return;
        const base = process.env.REACT_APP_BACKEND_URL;
        const abs = (u) => (u && u.startsWith('/') ? `${base}${u}` : u);
        setUploaded(
          (d.projects || []).map((p) => ({
            slug: p.slug,
            brand: p.brand,
            categories: p.categories || [],
            year: p.year || null,
            featured: !!p.featured,
            name: p.name?.[lang] || p.name?.pt || '',
            tagline: p.tagline && (p.tagline.pt || p.tagline.en) ? p.tagline : null,
            cover: abs(p.cover),
            coverThumb: abs(p.coverThumb),
            videos: (p.media || []).filter((m) => m.media_type === 'video'),
          }))
        );
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, [lang]);

  const catLabels = Object.fromEntries(PROJECT_CATEGORIES.map((c) => [c.key, c[lang]]));
  // O backend já devolve a ordem final (destaques primeiro, depois ordem manual do Admin)
  const visible = uploaded.filter((p) => cat === 'all' || p.categories.includes(cat));

  return (
    <div data-testid="portfolio-page">
      <section className="mx-auto max-w-[1600px] px-5 pb-10 pt-40 sm:px-8 sm:pt-48">
        <Kicker>{t.portfolio.kicker}</Kicker>
        <h1 className="mt-6 font-heading text-6xl font-black uppercase tracking-tight sm:text-8xl">
          <MaskedLines delay={0.1} lines={[<span key="w" className="brand-text">{t.portfolio.title}</span>]} />
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-6 max-w-xl text-zinc-400">{t.portfolio.desc}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1600px] px-5 pb-24 sm:px-8 sm:pb-32">
        <Reveal delay={0.1} className="mb-12 flex flex-wrap gap-2" data-testid="pf-filters">
          {PROJECT_CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCat(c.key)}
              data-testid={`pf-filter-${c.key}`}
              aria-pressed={cat === c.key}
              className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${
                cat === c.key
                  ? 'border-fl-yellow bg-fl-yellow text-black'
                  : 'border-zinc-800 text-zinc-400 hover:border-zinc-500 hover:text-white'
              }`}
            >
              {c[lang]}
            </button>
          ))}
        </Reveal>

        <motion.div layout className="grid grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 xl:grid-cols-3" data-testid="pf-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.div
                layout
                key={p.slug}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <ProjectCard p={p} catLabels={catLabels} lang={lang} viewLabel={t.cursor.open} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {loaded && visible.length === 0 && (
          <p
            className="border border-dashed border-zinc-800 p-12 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-600"
            data-testid="pf-empty"
          >
            {t.portfolio.empty}
          </p>
        )}
      </section>
    </div>
  );
}
